import { useEffect, useRef } from "react";
import { SelectOptions } from "../../../../internal/components/SelectOptions";
import { OptionsPanelHandle } from "../../../../internal/components/OptionsPanel";
import { SelectFieldTrigger } from "./SelectFieldTrigger";
import { SelectFieldLabel } from "./SelectFieldLabel";
import styles from "../../../../components/SelectTrigger/internal/SelectTriggerBase.module.scss";
import { useSelectMode } from "./useSelectMode";
import { OptionsPopover } from "../../../../internal/components/OptionsPopover/OptionsPopover";
import { OptionsDialog } from "../../../../internal/components/OptionsDialog/OptionsDialog";
import { FieldMessage } from "../../../../components/FieldMessage";
import { SelectFieldBaseInternalProps } from "../../../../internal/types/selectFieldInternalTypes";

/**
 * Select mode: non-searchable select using useSelect.
 * Active when disableSearch is true.
 */
export const SelectFieldSelectMode = ({
  labelAiMark,
  id,
  helperUid,
  selectedOption,
  onSelectedOptionChange,
  displayAs,
  isDisabledOrReadOnly,
  disabled,
  readOnly,
  placeholder,
  size,
  error,
  hasHelperText,
  disableClearButton,
  prefix,
  suffix,
  label,
  hideLabel,
  hint,
  errorMessages,
  warning,
  description,
  className,
  style,
  layoutStyles,
  pinned,
  groupToString,
  groupSorter,
  virtualize,
  options,
  loading,
  loadingMore,
  hasMore,
  loadOptions,
  loadMore,
  initialLoad,
  initialLoadPerformed,
  setInitialLoadPerformed,
  inputWrapperRef,
  required,
}: SelectFieldBaseInternalProps) => {
  const listRef = useRef<OptionsPanelHandle>(null);

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    wrapperDivRef,
    highlightedIndex,
    getItemProps,
    items,
    closeMenu,
    sectionsMeta,
  } = useSelectMode({
    displayAs,
    disabled: isDisabledOrReadOnly,
    options,
    pinned,
    groupToString,
    groupSorter,
    selectedOption,
    virtualize,
    onSelectedOptionChange: (opt) => onSelectedOptionChange(opt),
    async onIsOpenChange(changes) {
      if (initialLoad === "open" && !initialLoadPerformed && changes.isOpen) {
        await loadOptions("", { initial: true });
        setInitialLoadPerformed(true);
      }
    },
    onScrollToHighlight: (i) => listRef.current?.scrollToIndex(i),
  });

  useEffect(() => {
    const performInitialLoad = async () => {
      if (initialLoad === "immediate" && !initialLoadPerformed) {
        await loadOptions("", { initial: true });
        setInitialLoadPerformed(true);
      }
    };
    performInitialLoad();
  }, [loadOptions, initialLoadPerformed, initialLoad, setInitialLoadPerformed]);

  const handleLoadMore = async () => {
    await loadMore("");
  };

  const toggleButtonProps = getToggleButtonProps({
    disabled: !!disabled,
    "aria-required": required ? true : undefined,
    "aria-describedby": hasHelperText ? helperUid : undefined,
    "aria-invalid": error ? true : undefined,
  } as Record<string, unknown>);

  if (displayAs === "dialog") {
    return (
      <div
        className={
          className
            ? `${styles["search-field"]} ${className}`
            : styles["search-field"]
        }
        style={{ ...layoutStyles, ...style }}
        ref={wrapperDivRef}
      >
        <SelectFieldLabel
          aiMark={labelAiMark}
          label={label}
          hideLabel={hideLabel}
          getLabelProps={getLabelProps}
          required={required}
        />
        <SelectFieldTrigger
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          disableClearButton={disableClearButton}
          toggleButtonProps={toggleButtonProps}
          onClear={() => onSelectedOptionChange(null)}
          id={`${id}-trigger`}
          inputWrapperRef={inputWrapperRef}
          displayValue={selectedOption?.label}
          size={size}
          error={!!error}
          prefix={prefix}
          suffix={suffix}
        />
        {hasHelperText ? (
          <FieldMessage
            id={helperUid}
            hint={hint}
            error={errorMessages}
            warning={warning}
            description={description}
          />
        ) : null}
        <OptionsDialog
          id={`${id}-dialog`}
          isOpen={isOpen}
          onClose={closeMenu}
          title={label}
        >
          <SelectOptions
            ref={listRef}
            isOpen
            items={items}
            sectionsMeta={sectionsMeta}
            getMenuProps={(opts) =>
              getMenuProps({
                ...opts,
                style: { ...opts?.style, outline: "none" },
                onKeyDown: toggleButtonProps.onKeyDown,
                tabIndex: 0,
                "aria-activedescendant":
                  toggleButtonProps["aria-activedescendant"],
              })
            }
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            selectedOption={selectedOption}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            loading={loading}
            loadingMore={loadingMore}
            disabled={isDisabledOrReadOnly}
            virtualize={virtualize}
          />
        </OptionsDialog>
      </div>
    );
  }

  return (
    <div
      className={
        className
          ? `${styles["search-field"]} ${className}`
          : styles["search-field"]
      }
      style={{ ...layoutStyles, ...style }}
    >
      <SelectFieldLabel
        aiMark={labelAiMark}
        label={label}
        hideLabel={hideLabel}
        getLabelProps={getLabelProps}
        required={required}
      />
      <SelectFieldTrigger
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        disableClearButton={disableClearButton}
        toggleButtonProps={toggleButtonProps}
        onClear={() => onSelectedOptionChange(null)}
        id={`${id}-trigger`}
        popoverTarget={`${id}-popover`}
        inputWrapperRef={inputWrapperRef}
        displayValue={selectedOption?.label}
        size={size}
        error={!!error}
        prefix={prefix}
        suffix={suffix}
      />
      {hasHelperText ? (
        <FieldMessage
          id={helperUid}
          hint={hint}
          error={errorMessages}
          warning={warning}
          description={description}
        />
      ) : null}

      <OptionsPopover
        id={`${id}-popover`}
        referenceElement={inputWrapperRef}
        open={isOpen && displayAs === "popover"}
        onClickOutside={closeMenu}
        width="reference"
      >
        <SelectOptions
          ref={listRef}
          isOpen={isOpen}
          items={items}
          sectionsMeta={sectionsMeta}
          getMenuProps={getMenuProps}
          getItemProps={getItemProps}
          highlightedIndex={highlightedIndex}
          selectedOption={selectedOption}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          loading={loading}
          loadingMore={loadingMore}
          disabled={isDisabledOrReadOnly}
          virtualize={virtualize}
        />
      </OptionsPopover>
    </div>
  );
};
